import { useRef, useState } from 'react'
import { useStore } from '../store/AppStore'
import { usePlayer } from '../store/PlayerContext'
import { fmtClock, relDay, toISO } from '../lib/date'
import { Markdown } from '../lib/markdown'
import { Button, Card, Empty, Meta, ScreenHead, hueVars } from '../components/ui'
import { IconPause, IconPlay, IconPlus, IconTrash } from '../components/icons'
import type { WikiPage } from '../store/types'
import { useT } from '../i18n'

type Tab = 'music' | 'wiki' | 'notes'

export default function Library() {
  const [tab, setTab] = useState<Tab>('music')
  const t = useT()
  return (
    <>
      <ScreenHead title={t('library.title')} sub={t('library.subtitle')} />
      <div className="tabs" role="tablist" aria-label={t('library.title')}>
        {(
          [
            ['music', t('library.tabMusic')],
            ['wiki', t('library.tabWiki')],
            ['notes', t('library.tabNotes')],
          ] as const
        ).map(([id, label]) => (
          <button
            key={id}
            role="tab"
            className="tabs__tab"
            aria-selected={tab === id}
            onClick={() => setTab(id)}
          >
            {label}
          </button>
        ))}
      </div>
      {tab === 'music' && <MusicTab />}
      {tab === 'wiki' && <WikiTab />}
      {tab === 'notes' && <NotesTab />}
    </>
  )
}

/* ================================ music ================================== */

function MusicTab() {
  const { state, addTrack, removeTrack } = useStore()
  const player = usePlayer()
  const t = useT()
  const fileRef = useRef<HTMLInputElement>(null)
  const [filter, setFilter] = useState<'all' | 'audition'>('all')

  const tracks = filter === 'audition' ? state.tracks.filter((t) => t.audition) : state.tracks

  const onFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    const url = URL.createObjectURL(file)
    const audio = new Audio(url)
    audio.onloadedmetadata = () => {
      const id = addTrack({
        title: file.name.replace(/\.[^.]+$/, ''),
        artist: t('library.myUpload'),
        durationSec: Math.round(audio.duration) || 0,
        hue: 'clay',
        url,
        audition: true,
      })
      player.play(id)
    }
    e.target.value = ''
  }

  return (
    <div className="stack">
      <div className="row">
        <div className="row grow" style={{ gap: 'var(--s-2)' }}>
          {(['all', 'audition'] as const).map((f) => (
            <button key={f} className="chip" aria-pressed={filter === f} onClick={() => setFilter(f)}>
              {f === 'all' ? t('library.allTracks') : t('library.auditionOnly')}
            </button>
          ))}
        </div>
        <Button variant="ghost" size="sm" onClick={() => fileRef.current?.click()}>
          <IconPlus /> {t('library.upload')}
        </Button>
        <input
          ref={fileRef}
          type="file"
          accept="audio/*"
          onChange={onFile}
          className="sr-only"
          aria-label={t('library.uploadLabel')}
        />
      </div>

      <p className="setting__hint">{t('library.uploadHint')}</p>

      <Card className="card--flush" style={{ padding: 'var(--s-2)' }}>
        {tracks.length === 0 ? (
          <Empty title={t('library.nothingHere')} body={t('library.nothingHereBody')} />
        ) : (
          tracks.map((track) => {
            const isCurrent = player.current?.id === track.id
            return (
              <div
                key={track.id}
                className={`track ${isCurrent ? 'track--current' : ''}`}
                style={hueVars(track.hue)}
              >
                <button
                  className="track__art"
                  style={{ background: `var(--h-${track.hue})` }}
                  onClick={() => (isCurrent ? player.toggle() : player.play(track.id))}
                  aria-label={
                    isCurrent && player.playing
                      ? t('library.pauseNamed', { name: track.title })
                      : t('library.playNamed', { name: track.title })
                  }
                >
                  {isCurrent && player.playing ? <IconPause /> : <IconPlay />}
                </button>
                <div className="grow">
                  <div className="track__title truncate">
                    {track.title}
                    {track.audition && (
                      <span
                        className="countdown"
                        style={{ marginInlineStart: 6, color: 'var(--tint)' }}
                      >
                        {t('library.auditionBadge')}
                      </span>
                    )}
                  </div>
                  <div className="track__artist truncate">{track.artist}</div>
                </div>
                <span className="countdown">{fmtClock(track.durationSec)}</span>
                <Button
                  icon
                  variant="quiet"
                  onClick={() => {
                    if (isCurrent) player.stop()
                    removeTrack(track.id)
                  }}
                  aria-label={t('library.removeNamed', { name: track.title })}
                >
                  <IconTrash />
                </Button>
              </div>
            )
          })
        )}
      </Card>
    </div>
  )
}

/* ================================= wiki ================================== */

function WikiTab() {
  const { state, addWiki, updateWiki, removeWiki } = useStore()
  const t = useT()
  const [openId, setOpenId] = useState<string | undefined>(state.wiki[0]?.id)
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState('')

  const page: WikiPage | undefined = state.wiki.find((p) => p.id === openId) ?? state.wiki[0]

  if (!page)
    return (
      <div className="stack">
        <Empty title={t('library.noPages')} body={t('library.noPagesBody')} />
        <Button
          variant="accent"
          block
          onClick={() => setOpenId(addWiki({ title: t('library.untitledPage') }))}
        >
          <IconPlus /> {t('library.newPage')}
        </Button>
      </div>
    )

  return (
    <div className="stack">
      <div className="scroller">
        {state.wiki.map((p) => (
          <button
            key={p.id}
            className="chip"
            aria-pressed={p.id === page.id}
            onClick={() => {
              setOpenId(p.id)
              setEditing(false)
            }}
          >
            {p.title}
          </button>
        ))}
        <button
          className="chip"
          onClick={() => {
            const title = t('library.untitledPage')
            const id = addWiki({ title })
            setOpenId(id)
            setEditing(true)
            setDraft(`# ${title}\n\n`)
          }}
        >
          {t('library.newPageShort')}
        </button>
      </div>

      <Card>
        <div className="row" style={{ marginBottom: 'var(--s-2)' }}>
          <div className="grow">
            <h3
              style={{
                fontFamily: 'var(--font-display)',
                fontSize: 'var(--t-lg)',
                fontWeight: 600,
                lineHeight: 1.2,
              }}
            >
              {page.title}
            </h3>
            <Meta>
              {page.tag} · {t('library.editedRel', { when: relDay(toISO(new Date(page.updatedAt))) })}
            </Meta>
          </div>
          {editing ? (
            <>
              <Button
                variant="accent"
                size="sm"
                onClick={() => {
                  // The first `# heading` line doubles as the page title.
                  const heading = draft.match(/^#\s+(.+)$/m)?.[1]?.trim()
                  updateWiki(page.id, { body: draft, ...(heading ? { title: heading } : {}) })
                  setEditing(false)
                }}
              >
                {t('common.save')}
              </Button>
              <Button variant="quiet" size="sm" onClick={() => setEditing(false)}>
                {t('common.cancel')}
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setDraft(page.body)
                  setEditing(true)
                }}
              >
                {t('common.edit')}
              </Button>
              <Button
                icon
                variant="quiet"
                aria-label={t('study.deleteNamed', { name: page.title })}
                onClick={() => {
                  removeWiki(page.id)
                  setOpenId(state.wiki.find((p) => p.id !== page.id)?.id)
                  setEditing(false)
                }}
              >
                <IconTrash />
              </Button>
            </>
          )}
        </div>

        {editing && (
          <>
            <textarea
              className="textarea"
              dir="auto"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              aria-label={t('library.markdownSource')}
              style={{
                fontFamily: 'var(--font-mono)',
                fontSize: 'var(--t-xs)',
                minHeight: 220,
                marginBottom: 'var(--s-4)',
              }}
            />
            <Meta>{t('library.livePreview')}</Meta>
          </>
        )}

        <div style={{ marginTop: editing ? 'var(--s-3)' : 0 }}>
          <Markdown source={editing ? draft : page.body} />
        </div>
      </Card>
    </div>
  )
}

/* ================================ notes ================================== */

function NotesTab() {
  const { state, setQuickNote, pinNote, removeNote } = useStore()
  const t = useT()

  return (
    <div className="stack">
      <Card className="stack stack--sm">
        <Meta>{t('library.quickNote')}</Meta>
        <textarea
          className="textarea"
          dir="auto"
          placeholder={t('library.quickNotePlaceholder')}
          aria-label={t('library.quickNote')}
          value={state.quickNote}
          onChange={(e) => setQuickNote(e.target.value)}
          style={{ fontFamily: 'var(--font-display)' }}
        />
        <Button
          variant="accent"
          style={{ alignSelf: 'flex-start' }}
          onClick={pinNote}
          disabled={!state.quickNote.trim()}
        >
          {t('library.pinNote')}
        </Button>
      </Card>

      {state.notes.length === 0 ? (
        <Empty title={t('library.nothingPinned')} body={t('library.nothingPinnedBody')} />
      ) : (
        <div className="bento">
          {state.notes.map((n) => (
            <div key={n.id} className="note-card" style={hueVars(n.hue)}>
              <div className="note-card__text" dir="auto">
                {n.text}
              </div>
              <div className="row">
                <span className="countdown grow">{relDay(toISO(new Date(n.createdAt)))}</span>
                <Button
                  icon
                  variant="quiet"
                  onClick={() => removeNote(n.id)}
                  aria-label={t('library.deleteNote')}
                >
                  <IconTrash />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
